import styled from 'styled-components'
import {
  Button,
  Link,
  TextField,
} from '@mui/material'
import { Brand } from '../../components'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import loginImg from '../../assets/login.png'
import { RequestService } from '../../services/RequestService'
import useSignIn from 'react-auth-kit/hooks/useSignIn'
import { defineAuth } from '../../services/axiosInstance'
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated'
import useAuthUser from 'react-auth-kit/hooks/useAuthUser'
import i18n from '../../i18n.js'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`

const CarouselContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 400px;
  background: linear-gradient(#ea8e54, #d35f17);
  border-top-right-radius: 32px;
  border-bottom-right-radius: 32px;
  padding: 50px;
`

const CarouselContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;

  > span {
    margin-bottom: 8px;
  }
`

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: 24px;
  max-width: 500px;
`
const FormStyled = styled.form`
  padding: 48px 0;
  border-bottom: solid 1px #dde1e6;
`

const FormInputContainer = styled.div`
  display: flex;
  flex-direction: column;

  > div + div {
    margin-top: 24px;
  }

  > label {
    margin: 24px 8px;
  }
`

const ButtonStyled = styled(Button)`
  width: 134px;
`

const NoAccessContainer = styled.div`
  display: flex;
  padding-top: 48px;

  > span {
    margin-right: 4px;
  }
`

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  margin-top: 100px;
  max-width: 450px;
`

const Login = () => {
  const [loginKey, setLoginKey] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const signIn = useSignIn()
  const navigate = useNavigate()
  const isAuthenticated = useIsAuthenticated()
  const auth = useAuthUser()
  const { t } = useTranslation('common');

  const redirectUserTo = ({ onboardingPending, pvtPending, intakePending }) => {
    if (onboardingPending) {
      navigate('/onboarding')
    } else if (pvtPending && intakePending) {
      navigate('/redirect_options')
    } else if (intakePending) {
      navigate('/intake')
    } else {
      navigate('/dashboard')
    }
  }

  const handleLoginTyping = e => {
    if (loginError) setLoginError(false)

    setLoginKey(e.target.value)
  }

  const handlePasswordTyping = e => {
    if (loginError) setLoginError(false)

    setPassword(e.target.value)
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setIsLoading(true)

    const { status, data } = await RequestService.login({
      loginKey,
      password,
    })

    if (status != RequestService.STATUSES.SUCCESS) {
      setLoginError(true)
      setIsLoading(false)
      return
    }

    const {
      token,
      pvt_pending,
      onboarding_pending,
      intake_pending,
      missing_pvt_count,
      user_data,
    } = data

    defineAuth({ token })

    signIn({
      auth: {
        token,
        type: 'Bearer',
      },
      userState: {
        pvtPending: pvt_pending,
        onboardingPending: onboarding_pending,
        intakePending: intake_pending,
        userName: user_data?.name,
        missingPVTCount: missing_pvt_count,
        locale: user_data?.locale,
      },
    })

    if (user_data?.locale) {
      i18n.changeLanguage(user_data.locale)
    }

    setIsLoading(false)

    redirectUserTo({
      onboardingPending: onboarding_pending,
      pvtPending: pvt_pending,
      intakePending: intake_pending
    })
  }

  useEffect(() => {
    if (isAuthenticated) {
      redirectUserTo({
        onboardingPending: auth.onboardingPending,
        pvtPending: auth.pvtPending,
        intakePending: auth.intakePending
      })
    }
  })

  return (
    <Container>
      <CarouselContainer>
        <img
          width={296}
          height={296}
          src={loginImg}
          alt={t('login.carousel.image_alt')}
        />
        <CarouselContentContainer>
          <span
            style={{
              fontSize: 16,
              fontWeight: 600,
              fontStyle: 'italic',
              lineHeight: '24px',
              color: '#E2E4E7',
            }}
          >
            {t('login.carousel.message')}
          </span>
          <span style={{ fontSize: 12, lineHeight: '18px', color: '#E2E4E7' }}>
            {t('login.carousel.author')}
          </span>
        </CarouselContentContainer>
      </CarouselContainer>
      <LoginContainer>
        <Brand />
        <FormStyled onSubmit={handleSubmit}>
          <FormInputContainer>
            <TextField
              id='login'
              label={t("login.login")}
              placeholder={t("login.email_placeholder")}
              variant='outlined'
              size='small'
              value={loginKey}
              onChange={handleLoginTyping}
              error={loginError}
            />
            <TextField
              id='password'
              label={t("login.password")}
              placeholder={t("login.password_placeholder")}
              variant='outlined'
              type='password'
              size='small'
              value={password}
              onChange={handlePasswordTyping}
              error={loginError}
            />

            {loginError && (
              <div style={{ marginTop: 8 }}>
                <span style={{ fontSize: 14, color: '#BE3756' }}>
                  {t("login.login_error")}
                </span>
              </div>
            )}

            <div style={{ marginTop: 24 }}>
              <ButtonStyled
                type='submit'
                variant='contained'
                disabled={isLoading}
              >
                {!isLoading ? t("login.enter") : t("login.accessing")}
              </ButtonStyled>
            </div>
          </FormInputContainer>
        </FormStyled>
        {/* <NoAccessContainer>
          <span>Está sem acesso?</span>
          <span>Entre em contato com o administrador.</span>
        </NoAccessContainer> */}
         <Footer>
          <Link
            href='privacy-policy'
            target="_blank"
            underline='none'
            style={{ fontSize: 14, fontWeight: 600, color: '#666E7B' }}
          >
            {t("login.privacy_policy")}
          </Link>
          <Link
            href='terms-of-service'
            target="_blank"
            underline='none'
            style={{ fontSize: 14, fontWeight: 600, color: '#666E7B' }}
          >
            {t("login.terms_of_service")}
          </Link>
        </Footer>
      </LoginContainer>
    </Container>
  )
}

export default Login
